import PageWithNavbar from "@/components/PageWithNavbar";
import {
  TransparentCard,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";

interface FeatureProps {
  title: string;
  description: string;
  details: string;
  image: string | null;
  video: string | null;
}

const features: FeatureProps[] = [
  {
    title: "Unfettered Python Scripting",
    description:
      "Full Python support for custom strategy development and automation.",
    details:
      "Leverage the power of Python without constraints to build custom indicators and algorithmic trading bots. Our API documentation and examples make it easy to get started - and with phone support available, help is always just a call away.",
    image: "/python-sma.png",
    video: null,
  },
  {
    title: "Multi-Exchange Integration",
    description:
      "Connect to all major US-regulated exchanges through a single unified professional interface.",
    details:
      "Seamlessly connect to Coinbase, Kraken, Gemini, Binance US, and Crypto.com from one platform. Manage and execute trades across all of your accounts without switching between different applications. Our unified interface simplifies multi-exchange trading.",
    image: null,
    video: null,
  },
  {
    title: "Algorithmic Trading Suite",
    description:
      "Built-in Python support for creating and executing sophisticated trading algorithms.",
    details:
      "Develop custom trading strategies using Python with access to real-time market data and order execution capabilities. Execute complex strategies including market making, arbitrage, and momentum trading with minimal latency.",
    image: null,
    video: "/Algorithms-Start.webm",
  },
  /*
  {
    title: "Order Management System",
    description:
      "Professional-grade order management with advanced order types and execution strategies.",
    details:
      "Execute trades with precision using limit orders. Track order status in real-time, manage open positions, and maintain trail of trading activity across exchanges.",
    image: null,
    video: null,
  },
  */
  {
    title: "Real-Time Market Data",
    description:
      "Live streaming data feeds with microsecond precision for accurate market analysis.",
    details:
      "Access real-time price quotes, order book updates, and trade execution data with ultra-low latency. Our blazing fast software makes sure you never miss a market move.",
    image: null,
    video: null,
  },
  {
    title: "Advanced Depth of Book",
    description:
      "Access the most advanced and customizable depth of book available for cryptocurrency trading.",
    details:
      "Our depth of book visualization provides real-time order flow analysis and market microstructure insights. Customize the display across multiple exchanges simultaneously. Track large orders, identify support and resistance levels, and make informed trading decisions based on actual market liquidity.",
    image: null,
    video: "/Dob-Custom-Size.webm",
  },
  {
    title: "Account Monitoring",
    description:
      "Comprehensive portfolio tracking and performance analytics across all your accounts.",
    details:
      "Monitor your positions, balances, and P&L in real-time across multiple exchanges and accounts. All of your accounts are consolidated into a single dashboard for easy tracking.",
    image: null,
    video: null,
  },
  {
    title: "Self-Hosted Algorithm Server",
    description:
      "Take your Doyen scripts out of the main application and run them on dedicated servers, personal computers, or cloud instances.",
    details:
      "Run your trading algorithms headlessly on your own hardware using our Algorithm Server (Drasil). This lightweight application allows you to deploy and manage your Doyen scripts without the overhead of the full Doyen application, providing flexibility and scalability for serious traders.",
    image: "/client-cli-start.png",
    video: null,
  },
  {
    title: "Anonymous Accounts",
    description:
      "Optional privacy-focused account creation with minimal personal information required.",
    details:
      "Create accounts without linking to personal identities, using only secure authentication methods. Your trading activity and strategies remain private. We support privacy-conscious traders.",
    image: null,
    video: null,
  },
  {
    title: "Integrated News Feeds",
    description:
      "Stay informed with curated cryptocurrency news and market-moving events.",
    details:
      "Access real-time news feeds from major crypto news sources. Our news integration helps you stay ahead of market-moving events and make informed trading decisions.",
    image: null,
    video: "/Newsfeed-Filtering.webm"
  },
];

export const FeaturesPage = () => {
  return (
  <PageWithNavbar>
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold">
            Powerful{" "}
            <span className="bg-gradient-to-b from-primary/60 to-primary text-transparent bg-clip-text">
              Trading Features
            </span>
          </h1>
          <p className="text-xl text-muted-foreground mt-2 mb-6">
            Everything you need for professional cryptocurrency trading.
          </p>
        </div>

        <div >
          {features.map(({ title, description, details, image, video }: FeatureProps) => (
            <div className="flex flex-col items-center md:flex-row mb-12 gap-6" key={title}>
                {image && (
                <img
                  src={image}
                  alt={`${title} illustration`}
                  className="w-full max-w-[300px] md:max-w-[200px] lg:max-w-[600px] h-auto object-contain"
                />)}
                {video && (  
                  <video 
                  controls
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full max-w-[300px] md:max-w-[200px] lg:max-w-[600px] h-auto object-contain"
                  >
                      <source src={video} type="video/webm" />
                      Your browser does not support the video tag.
                  </video>
                )}
                <TransparentCard className="w-full md:w-2/3">
                  <CardHeader>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                  </CardHeader>

                  <CardContent >
                    <p className="text-muted-foreground">{details}</p>
                  </CardContent>
              </TransparentCard>
            </div>
          ))}
        </div>
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default FeaturesPage;
